import React, { useEffect, useState, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DaySpotAndRangePicker,
  Error,
  Loading,
  ManufacturingOrderExportExcelContainer,
  QuotationReport,
  QuotationTable,
  ViewTitle,
} from '@iarc-programing/emanufac-components/src/components';
import { Card, CardContent, Switch, TextField } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { config } from '@/configs';
import * as actions from '@/redux/actions';
import { QuotationUtils } from '@/utils/excelColumn';

const DatePickerSection = memo(
  ({
    allowTimeQuery,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    page,
    size,
  }) => {
    return (
      <DaySpotAndRangePicker
        addSearchQuery
        anotherQuery={`?page=${page}&size=${size}`}
        flex
        size="small"
        enable={allowTimeQuery}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        startDate={startDate}
        endDate={endDate}
      />
    );
  },
);
export default function QuotationLogs({ title, subtitle }) {
  const dispatch = useDispatch();

  const quotationLog = useSelector((state) => state.quotationLog);
  const information = useSelector((state) => state.information);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(undefined);

  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');

  const [allowTimeQuery, setAllowTimeQuery] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // ✅ คำนวณ effectiveEndDate: ถ้าไม่มี endDate ให้ใช้วันปัจจุบัน
  const effectiveEndDate = useMemo(() => {
    if (!allowTimeQuery || !startDate) return '';
    if (endDate && dayjs(endDate).isValid()) {
      return dayjs(endDate).format('YYYY-MM-DD');
    }
    // เลือกแค่ startDate → ใช้วันนี้เป็น endDate
    return dayjs().format('YYYY-MM-DD');
  }, [allowTimeQuery, startDate, endDate]);

  const effectiveStartDate = useMemo(() => {
    if (!allowTimeQuery || !startDate || !dayjs(startDate).isValid()) return '';
    return dayjs(startDate).format('YYYY-MM-DD');
  }, [allowTimeQuery, startDate]);

  const queryDataFromServer = async () => {
    try {
      dispatch(
        actions.quotationLogAll({
          page,
          size,
          name,
          startDate: effectiveStartDate,
          endDate: effectiveEndDate,
        }),
      );
    } catch (error) {
      dispatch(actions.quotationLogError());
    }
  };

  const handlePrintPdf = (row) => {
    try {
      QuotationReport(row, information, true);
    } catch (error) {
      QuotationReport(row, information);
    }
  };

  useEffect(() => {
    // ✅ ถ้าเปิด time query: ต้องมี startDate อย่างน้อย
    if (allowTimeQuery && startDate && !dayjs(startDate).isValid()) return;
    if (allowTimeQuery && !startDate) return;
    if (allowTimeQuery && !dayjs(startDate).isValid()) return;

    queryDataFromServer();
  }, [
    page,
    size,
    name,
    allowTimeQuery,
    effectiveStartDate, // ✅ trigger เมื่อ startDate เปลี่ยน
    effectiveEndDate,   // ✅ trigger เมื่อ endDate เปลี่ยน
  ]);

  useEffect(() => {
    if (quotationLog?.rows) {
      setTotal(quotationLog?.total);
    }
  }, [quotationLog]);

  // ✅ ป้องกัน endDate ก่อน startDate
  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(startDate).isAfter(dayjs(endDate))
    ) {
      setEndDate(null);
    }
  }, [startDate]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const quotationQuery = {
    page: 1,
    size: config.maxFetchSize,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
  };

  const renderTitle = () => (
    <ViewTitle title={title} subtitle={subtitle} />
  );

  if (!quotationLog.isCompleted && !quotationLog.isLoading) {
    return <Error />;
  }

  return (
    <div>
      {quotationLog.isLoading && <Loading />}

      <div className="flex justify-between items-center">
        {renderTitle()}
      </div>

      <div className="my-2">
        <Card>
          <CardContent>
            <div className="flex flex-wrap justify-between">
              <div className="w-full md:w-1/2 lg:w-1/3">
                <TextField
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  label="ค้นหา"
                  fullWidth
                />
              </div>

              <div className="w-1/2 lg:w-1/6 ml-4 flex justify-end">
                <ManufacturingOrderExportExcelContainer
                  sheetName="Quotation"
                  dataQuery={quotationQuery}
                  currentData={quotationLog?.rows}
                  dataAPIEndPoint="quotation-log"
                  columnList={QuotationUtils.columns}
                  processColumnList={QuotationUtils.columnWithProcess}
                  spreadedColumn="processes"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="my-2">
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-center">
              <div className="w-full lg:w-1/3 flex items-center">
                <Switch
                  checked={allowTimeQuery}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAllowTimeQuery(checked);
                    if (!checked) {
                      setStartDate(null);
                      setEndDate(null);
                    }
                  }}
                />
                <div>เปิดใช้การกรองเวลา</div>
              </div>

              <div className="w-full lg:w-2/3">
                <DatePickerSection
                  allowTimeQuery={allowTimeQuery}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  page={page}
                  size={size}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="my-2">
        <QuotationTable
          quotation={quotationLog}
          page={page}
          size={size}
          total={total}
          setPage={setPage}
          setSize={setSize}
          handleDelete={() => { }}
          handlePrintPdf={handlePrintPdf}
        />
      </div>
    </div>
  );
}

QuotationLogs.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

QuotationLogs.defaultProps = {
  title: '',
  subtitle: '',
};